import { OTHER_TEXT_MAX_LENGTH } from "@shared/types";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import { Chip, FieldInput } from "@/features/studio/theme";
import { FieldGroupLabel, FieldHint } from "./field-label";

export interface ChipOption {
	value: string;
	label: string;
}

interface OptionChipsProps {
	label: string;
	options: readonly ChipOption[];
	selected: readonly string[];
	onChange: (next: string[]) => void;
	/** One choice at a time instead of many. */
	single?: boolean;
	hint?: string;
	/**
	 * The option value that opens a short text box when chosen (Decision 8 on
	 * #362). Omit when the list has no "Other".
	 */
	otherValue?: string;
	otherText?: string;
	onOtherTextChange?: (text: string) => void;
	/** Label of the text box; defaults to "Other (please specify)". */
	otherLabel?: string;
	/** Tailwind grid classes; chips wrap in a flex row when omitted. */
	gridClassName?: string;
	/** Keep the label for screen readers only, e.g. when a heading says it already. */
	hideLabel?: boolean;
}

/**
 * A labelled group of toggle chips. Multi-select by default; `single` makes a
 * second click on the chosen chip clear it, and a click elsewhere move it.
 */
export function OptionChips({
	label,
	options,
	selected,
	onChange,
	single = false,
	hint,
	otherValue,
	otherText = "",
	onOtherTextChange,
	otherLabel,
	gridClassName,
	hideLabel = false,
}: OptionChipsProps) {
	const { t } = useTranslation();
	const otherId = useId();

	const toggle = (value: string) => {
		if (single) {
			onChange(selected.includes(value) ? [] : [value]);
			return;
		}
		onChange(
			selected.includes(value)
				? selected.filter((item) => item !== value)
				: [...selected, value],
		);
	};

	const showOther = otherValue !== undefined && selected.includes(otherValue);
	const chipStyle = gridClassName
		? { justifyContent: "center", height: "auto", minHeight: 40 }
		: undefined;

	return (
		<fieldset className="min-w-0">
			<FieldGroupLabel
				as="legend"
				className={hideLabel ? "sr-only" : undefined}
			>
				{label}
			</FieldGroupLabel>
			<div className={gridClassName ?? "flex flex-wrap gap-2"}>
				{options.map((option) => (
					<Chip
						key={option.value}
						active={selected.includes(option.value)}
						onClick={() => toggle(option.value)}
						style={chipStyle}
					>
						{option.label}
					</Chip>
				))}
			</div>
			{hint && <FieldHint>{hint}</FieldHint>}
			{showOther && (
				<FieldInput
					id={otherId}
					className="mt-3"
					label={otherLabel ?? t("onboarding.fields.otherLabel")}
					value={otherText}
					maxLength={OTHER_TEXT_MAX_LENGTH}
					onChange={(event) => onOtherTextChange?.(event.target.value)}
					placeholder={t("onboarding.fields.otherPlaceholder")}
				/>
			)}
		</fieldset>
	);
}
